import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Logo } from "@/components/Brand";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, AlertTriangle } from "lucide-react";

const AUTH_BG =
  "https://images.unsplash.com/photo-1542903660-eedba2cda473?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHw0fHxjeWJlcnNlY3VyaXR5JTIwZGF0YSUyMGFic3RyYWN0fGVufDB8fHxibGFja3wxNzg0MDkyNjE0fDA&ixlib=rb-4.1.0&q=85";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }
    setLoading(true);
    try {
      await register(name, email, password);
      navigate("/app");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="relative hidden lg:block scanlines">
        <div className="absolute inset-0 bg-cover bg-center opacity-40" style={{ backgroundImage: `url(${AUTH_BG})` }} />
        <div className="absolute inset-0 bg-gradient-to-tr from-background via-background/70 to-transparent" />
        <div className="relative h-full flex flex-col justify-between p-12">
          <Logo />
          <div>
            <h2 className="font-mono font-bold tracking-tighter text-4xl leading-tight">
              Deploy your <span className="text-primary text-glow">defense grid</span>
            </h2>
            <p className="mt-4 text-muted-foreground max-w-sm">
              Create an account to protect your systems from malware, trojans, phishing and every emerging cyber threat.
            </p>
          </div>
          <div className="font-mono text-xs text-muted-foreground">// zero-trust onboarding</div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm">
          <div className="lg:hidden mb-8"><Logo /></div>
          <div className="font-mono text-xs uppercase tracking-[0.25em] text-primary mb-2">// Enrollment</div>
          <h1 className="font-mono font-bold tracking-tighter text-3xl mb-8">Create account</h1>

          {error && (
            <div data-testid="register-error" className="flex items-start gap-2 border border-destructive/50 bg-destructive/10 text-destructive px-4 py-3 mb-6 font-mono text-sm">
              <AlertTriangle size={16} strokeWidth={1.5} className="mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={submit} className="space-y-5">
            <div>
              <Label className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Name</Label>
              <Input
                data-testid="register-name"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Ada Lovelace"
                className="mt-2 rounded-none bg-input border-border font-mono focus-visible:ring-primary focus-visible:ring-2"
              />
            </div>
            <div>
              <Label className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Email</Label>
              <Input
                data-testid="register-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@domain.com"
                className="mt-2 rounded-none bg-input border-border font-mono focus-visible:ring-primary focus-visible:ring-2"
              />
            </div>
            <div>
              <Label className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Password</Label>
              <Input
                data-testid="register-password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="min. 6 characters"
                className="mt-2 rounded-none bg-input border-border font-mono focus-visible:ring-primary focus-visible:ring-2"
              />
            </div>
            <Button
              data-testid="register-submit"
              type="submit"
              disabled={loading}
              className="w-full rounded-none font-mono bg-primary text-primary-foreground hover:bg-primary/90 glow-green"
            >
              {loading ? <Loader2 size={16} className="animate-spin" /> : "Create secure account"}
            </Button>
          </form>

          <p className="mt-6 text-sm text-muted-foreground font-mono">
            Already protected?{" "}
            <Link to="/login" data-testid="goto-login" className="text-primary hover:underline">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
